import axios from 'axios';
import {Buffer} from 'buffer';
import crypto from 'crypto';
import firebase from 'firebase/compat/app';

import {
  LEAGUES,
  SPORTS,
  VENDOR_IDS,
} from '@chancer/common/lib/interfaces/firestore/FirestoreEnums';
import {
  CompQuestionType,
  CompStatus,
  LeaderboardType,
  TComp,
  TCompCounts,
  TCompQuestion,
  TCompStatusUpdated,
  TLeaderboard,
  TMediaEntry,
} from '@chancer/common/lib/interfaces/firestore/FirestoreInterfaces';
import {
  TLocalComp,
  getLocalLeaderboardImageFileName,
  getLocalLeaderboardPrizeImageFileName,
  getLocalQuestionAuthorFileName,
  getLocalQuestionImageFileName,
  getLocalQuestionLinkFileName,
  getLocalQuestionPublisherFileName,
  getQuestionNumberFromImageName,
} from '@chancer/common/lib/interfaces/firestore/LocalJsonInterfaces';
import {assertType, cleanCast} from '@chancer/common/lib/utils/TypeUtils';

import {convert} from '@chancer/common/lib/utils/FirebaseUtils';
import {IConfig} from '../../../config/Config';
import {
  addCompetitionQuestions,
  removeAllQuestionsFromComp,
} from '../../../utils/FirestoreUtils';
import {uploadAndConfigureLeaderboards} from './LeaderboardUploader';

export const verifyCompJson = async (
  config: IConfig,
  compJsonString: string,
  log: (message: string) => void,
) => {
  log('Verifying comp.json:\n');
  const readComp = cleanCast(
    TLocalComp,
    JSON.parse(compJsonString, (key, value) => {
      if (typeof value === 'string') {
        if (
          /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d*)?)(Z|([+-])(\d{2}):(\d{2}))$/.exec(
            value,
          )
        ) {
          return firebase.firestore.Timestamp.fromDate(new Date(value));
        }
      }
      return value;
    }),
  );
  if (!readComp) {
    throw new Error('Unable to load competition from comp.json.');
  }

  log('Valid competition JSON format ✅\n');
  checkRequiredProperty('shareDetails', readComp.shareDetails, log);
  checkRequiredProperty('key', readComp.key, log);
  readComp.key =
    readComp.template === true ? readComp.key : readComp.key.toLowerCase();
  checkRequiredProperty('vendor', readComp.vendor, log);
  readComp.vendor = readComp.vendor.toLowerCase();
  checkRequiredProperty(
    'vendor is valid',
    VENDOR_IDS.find((v) => v === readComp.vendor),
    log,
    `The vendor ${readComp.vendor} is not on the valid vendors list`,
  );
  checkRequiredProperty('sport', readComp.sport, log);
  checkRequiredProperty(
    'sport is valid',
    SPORTS.find((v) => v === readComp.sport),
    log,
    `The sport ${readComp.sport} is not on the valid sports list`,
  );
  checkRequiredProperty('league', readComp.league, log);
  checkRequiredProperty(
    'league is valid',
    LEAGUES.find((v) => v === readComp.league),
    log,
    `The league ${readComp.league} is not on the valid leagues list`,
  );
  checkRequiredProperty('shortName', readComp.shortName, log);
  checkRequiredProperty('roundName', readComp.roundName, log);
  checkRequiredProperty('promo', readComp.promo, log);
  checkRequiredProperty(
    'privateData',
    readComp.privateData,
    log,
    'You need to add the `privateData` node to comp.json.',
  );
  checkRequiredProperty(
    'privateData.nativeNotifications',
    readComp.privateData.nativeNotifications,
    log,
    'You need to add the `privateData.nativeNotifications` node to comp.json.',
  );
  checkRequiredProperty(
    'privateData.smsNotifications',
    readComp.privateData.smsNotifications,
    log,
    'You need to add the `privateData.smsNotifications` node to comp.json.',
  );
  checkRequiredProperty(
    'privateData.emailNotifications',
    readComp.privateData.emailNotifications,
    log,
    'You need to add the `privateData.emailNotifications` node to comp.json.',
  );
  checkRequiredProperty(
    'signUpCompleteView',
    readComp.signUpCompleteView,
    log,
    'You need to add the `signUpCompleteView` node to comp.json.',
  );
  checkRequiredProperty(
    'leaderboardConfigurations',
    readComp.leaderboardConfigurations,
    log,
  );
  if (
    readComp.leaderboardConfigurations.filter(
      (lb) => lb.type === LeaderboardType.INGAME,
    ).length !== 1
  ) {
    throw new Error(
      "There should be exactly 1 ingame leaderboard associated with a comp'",
    );
  }

  if (
    readComp.privateData.nativeNotifications.templateGameOpen?.title.includes(
      '<%',
    ) ||
    readComp.privateData.nativeNotifications.templateGameOpen?.body.includes(
      '<%',
    )
  ) {
    log(
      '\n❗❗❗ WARNING ❗❗❗ The text in privateData.nativeNotifications.templateGameOpen contain expandable template properties but this field is never expanded in the codebase. This will result in the user seeing the template string as is. \n\n',
    );
  }
  checkQuestions(readComp.questions.map((q) => q.question));
  await setEmailVendorListId(config, readComp, log);
  await setEmailIdFromTemplateName(config, readComp, 'templateGameEntry', log);
  await setEmailIdFromTemplateName(config, readComp, 'templateGameInPlay', log);
  await setEmailIdFromTemplateName(
    config,
    readComp,
    'templateGameVerified',
    log,
  );
  await setEmailIdFromTemplateName(config, readComp, 'templateGamePrize', log);
  return readComp;
};

export const verifyAndRetrieveMediaFiles = (
  comp: TLocalComp,
  files: File[],
  log: (message: string) => void,
  prefix: string = '',
) => {
  return verifyAndRetrieveMediaFilesInternal({
    comp,
    questions: comp.questions.map((q) => q.question),
    leaderboardConfigurations: comp.leaderboardConfigurations,
    files,
    required: true,
    log,
    prefix,
  });
};

export const verifyAndRetrieveMediaFilesInternal = (options: {
  comp: Pick<TComp, 'sponsor' | 'feature'>;
  questions: Pick<TCompQuestion, 'media' | 'link' | 'author'>[];
  leaderboardConfigurations: Pick<TLeaderboard, 'key' | 'prizes'>[];
  files: File[];
  required?: boolean;
  log?: (message: string) => void;
  prefix?: string;
}) => {
  const {
    comp,
    questions,
    leaderboardConfigurations,
    files,
    required = true,
    log = console.log,
    prefix = '',
  } = options;
  // Verify the required files
  log('Checking for the required media files\n');
  const keep: File[] = [];
  [
    'cover',
    'logo',
    ...(comp.sponsor?.caption ? ['sponsor'] : []),
    'promo',
    ...(comp.feature ? ['feature'] : []),
    'complete',
    'share',
    'signup-complete',
    // Questions image
    ...questions
      .map((q, i) =>
        q.media.social !== undefined ? null : getLocalQuestionImageFileName(i),
      )
      .filter((f): f is string => f !== null),
    // Question linked media image
    ...(questions
      .map((q, i) => (q.link?.caption ? getLocalQuestionLinkFileName(i) : null))
      .filter((f) => f !== null) as string[]),
    // Question linked media publisher image
    ...(questions
      .map((q, i) =>
        q.link?.publisher?.caption
          ? getLocalQuestionPublisherFileName(i)
          : null,
      )
      .filter((f) => f !== null) as string[]),
    // Question linked media author image
    ...(questions
      .map((q, i) =>
        q.author?.caption ? getLocalQuestionAuthorFileName(i) : null,
      )
      .filter((f) => f !== null) as string[]),
    ...leaderboardConfigurations.map((l, i) =>
      getLocalLeaderboardImageFileName(l.key),
    ),
    ...leaderboardConfigurations.reduce(
      (fileNames: string[], lbConfig: Pick<TLeaderboard, 'key' | 'prizes'>) => {
        return fileNames.concat(
          lbConfig.prizes.map((p, i) =>
            getLocalLeaderboardPrizeImageFileName(lbConfig.key, i),
          ),
        );
      },
      [],
    ),
  ].forEach((name) => {
    const namesToSearch = [`${prefix}${name}`, name];
    let found: File | undefined;
    for (let i = 0; i < namesToSearch.length; i++) {
      found = files.find((f) => fileSearch(f, namesToSearch[i]));
      if (found) {
        keep.push(found);
        break;
      }
    }
    if (!found && required) {
      throw new Error(`${namesToSearch.join(', ')} not found!`);
    }
  });
  log(' ✅\n');

  log('\nReady to upload comp using these media files only:\n');
  log(
    keep
      .map((file) => `${file.name} [${Math.round(file.size / 100000) / 10}M]`)
      .join('\n'),
  );
  return keep;
};

export const uploadCompetition = async (
  config: {
    protocol: string;
    adminDomain: string;
    cdnDomain: string;
    webDomain: string;
  },
  comp: TLocalComp,
  files: File[],
  log: (message: string) => void,
  prefix: string = '',
) => {
  const {
    questions,
    leaderboardConfigurations,
    privateData,
    compPrompt,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    created,
    ...compForUpload
  } = comp;
  let compRef = firebase.firestore().collection('competitions').doc();
  let existingPublishState = false;
  let isNew = true;

  const compQuerySnap = await firebase
    .firestore()
    .collection('competitions')
    .where('vendor', '==', comp.vendor)
    .where('key', '==', comp.key)
    .limit(1)
    .get();
  if (!compQuerySnap.empty && compForUpload.template !== true) {
    log(
      `Overwriting existing competition [${comp.vendor}/${comp.key}}] with id [${compQuerySnap.docs[0].id}].\n`,
    );
    isNew = false;
    compRef = compQuerySnap.docs[0].ref;
    log('Verifying that the existing competition has status DRAFT\n');
    let existingStatus: TCompStatusUpdated | undefined;
    try {
      existingStatus = convert(
        TCompStatusUpdated,
        await compRef.collection('comp_data').doc('status').get(),
      );
    } catch (err) {
      if ((err as any).message.includes('does not exist')) {
        log(
          'Unable to load current competitions status. Assuming DRAFT and creating status. \n',
        );
        await compRef
          .collection('comp_data')
          .doc('status')
          .set(
            assertType<TCompStatusUpdated>({
              status: CompStatus.DRAFT,
              updated: firebase.firestore.FieldValue.serverTimestamp() as any,
            }),
          );
      } else {
        // Prevent re-upload as we don't know what the status is
        console.warn(err);
        log(
          "Unable to load current competitions status and don't know reason. Assuming OPEN to prevent upload. Check console logs. \n",
        );
        existingStatus = {
          status: CompStatus.OPEN,
          updated: undefined as any,
        };
      }
    }
    if (existingStatus && existingStatus.status !== CompStatus.DRAFT) {
      throw new Error(
        `Competition [${compQuerySnap.docs[0].id}] is already using vendor [${comp.vendor}] and key [${comp.key}], but has status [${existingStatus.status}]. DRAFT status is required for an update.`,
      );
    }

    log(' ✅\n');
    existingPublishState = convert(TComp, compQuerySnap.docs[0]).published;

    let existingCount: TCompCounts | undefined;
    log('Verifying that the existing competition has no entries\n');
    try {
      existingCount = convert(
        TCompCounts,
        await compRef.collection('comp_data').doc('counts').get(),
      );
    } catch (err) {
      if ((err as any).message.includes('does not exist')) {
        log(
          'Unable to load current competitions counts. Assuming empty and creating counts. \n',
        );
        await compRef
          .collection('comp_data')
          .doc('counts')
          .set(
            assertType<TCompCounts>({
              numQuestions: comp.questions.length,
              answersBreakdown: {},
              likesBreakdown: {},
              entriesCount: 0,
              resolvedQuestions: [],
              resolutions: [],
            }),
          );
      } else {
        // Prevent re-upload as we don't know what the status is
        console.warn(err);
        log(
          "Unable to load current competitions counts and don't know reason. Assuming has users. Check console logs. \n",
        );
        existingCount = {
          entriesCount: 1,
          answersBreakdown: {},
          likesBreakdown: {},
          numQuestions: 1,
          resolvedQuestions: [],
          resolutions: [],
        };
      }
    }

    if (existingCount && existingCount.entriesCount > 0) {
      throw new Error(
        `Competition [${compQuerySnap.docs[0].id}] is already using vendor [${comp.vendor}] and key [${comp.key}], but has [${existingCount.entriesCount}] entries. You can't update a competition once it has entries.`,
      );
    }
    log(' ✅\n');
  } else {
    log(
      `Uploading new competition [${comp.vendor}/${comp.key}}] with id [${compRef.id}].\n`,
    );
  }

  const adminOrigin =
    config.protocol === 'http:'
      ? `http://${config.adminDomain}`
      : window.location.origin;

  log('Getting a new short url');
  let shortUrl:
    | {
        shortUrl: string;
        qrCodeUrl: string;
        shortUrlPlay: string;
        qrCodeUrlPlay: string;
      }
    | undefined;
  try {
    shortUrl = await (
      await axios.get(
        `${adminOrigin}/shrtn?id=${compRef.id}&vendor=${comp.vendor}&key=${comp.key}&title=${comp.name}&description=${comp.media.caption}`,
      )
    ).data;
    console.log('shortUrl', shortUrl);
  } catch (err) {
    log(' ❌\n');
    log('Unable to get a short url. Continuing without one.\n');
  }
  log(' ✅\n');

  let deployableComp: TComp = {
    ...compForUpload,
    created: firebase.firestore.Timestamp.fromDate(new Date()),
    shortUrl: shortUrl?.shortUrl ?? '',
    qrCode: shortUrl?.qrCodeUrl ?? '',
    shortUrlPlay: shortUrl?.shortUrlPlay ?? '',
    qrCodePlay: shortUrl?.qrCodeUrlPlay ?? '',
    leaderboards: [],
    published: existingPublishState || compForUpload.published,
  };

  // Write before uploading files if this is a new competition so
  // that we don't lose the id
  if (isNew) {
    log(
      `Reserving the competition id [${compRef.id}] before uploading media files`,
    );
    await compRef.set(deployableComp);
    log(' ✅\n');
  }

  log('\nProcessing leaderboards:\n');
  const leaderboardIds = await uploadAndConfigureLeaderboards(
    config,
    compRef.id,
    compForUpload.vendor,
    files,
    leaderboardConfigurations,
    prefix,
    log,
  );

  log('\nProcessing private data:\n');
  await compRef.collection('comp_data').doc('private').set(privateData);

  log('\nProcessing comp prompt:\n');
  if (compPrompt) {
    await compRef.collection('comp_data').doc('prompt').set(compPrompt);
  }

  log('Uploading competition media files:\n');
  await uploadAssetForComp(
    deployableComp,
    questions.map((q) => q.question),
    files,
    config,
    log,
    prefix,
  );

  // Fall back to question images for promo and cover if no dedicated files were provided
  if (
    !deployableComp.promo?.image?.url &&
    questions[0]?.question?.media?.image?.url
  ) {
    deployableComp.promo = {image: questions[0].question.media.image};
  }
  if (
    !deployableComp.media?.image?.url &&
    questions[1]?.question?.media?.image?.url
  ) {
    deployableComp.media = {image: questions[1].question.media.image};
  }

  log('Writing competition data\n');
  // Write the comp to Firestore with the the media values
  deployableComp = {
    ...deployableComp,
    leaderboards: leaderboardIds,
    published: existingPublishState || deployableComp.published,
  };
  await compRef.set(deployableComp);

  if (!isNew) {
    log('Removing any questions from the existing competition\n');
    const batch = firebase.firestore().batch();
    await removeAllQuestionsFromComp(batch, compRef.id);
    await batch.commit();
    log(' ✅\n');
  }

  log('Creating initial status doc\n');
  await compRef
    .collection('comp_data')
    .doc('status')
    .set(
      assertType<TCompStatusUpdated>({
        status: CompStatus.DRAFT,
        updated: firebase.firestore.FieldValue.serverTimestamp() as any,
      }),
    );
  log('Creating initial counts doc\n');
  await compRef
    .collection('comp_data')
    .doc('counts')
    .set(
      assertType<TCompCounts>({
        numQuestions: comp.questions.length,
        answersBreakdown: {},
        likesBreakdown: {},
        entriesCount: 0,
        resolvedQuestions: [],
        resolutions: [],
      }),
    );

  log('Adding questions to top level questions collection]');
  const questionsBatch = firebase.firestore().batch();
  await addCompetitionQuestions(questionsBatch, compRef.id, questions);
  await questionsBatch.commit();
  log(' ✅\n');

  log('\n🔥🔥🔥\n');
  log(`Short: ${deployableComp.shortUrl}\n`);
  log(
    `Verification: https://${config.webDomain}/${deployableComp.vendor}/${deployableComp.key}/?comp=${compRef.id}\n`,
  );
  log(
    `Go-live: https://${config.webDomain}/${deployableComp.vendor}/${deployableComp.key}/\n`,
  );
  log('🔥🔥🔥\n');
};

export const uploadAssetForComp = async (
  comp: TComp,
  questions: TCompQuestion[],
  files: File[],
  config: {protocol: string; cdnDomain: string},
  log: (message: string) => void,
  prefix: string = '',
) => {
  const logUploadProgress = (progress: number, message: string) => {
    log(message);
  };
  const checkFileName = (file: File, name: string): boolean => {
    const namesToSearch = [`${prefix}${name}`, name];
    return namesToSearch.some((n) => file.name.startsWith(n));
  };
  for (let i = 0; i < files.length; i++) {
    const newMediaEntry = await uploadAssetForDocument(
      config,
      files[i],
      logUploadProgress,
    );
    if (checkFileName(files[i], 'cover')) {
      comp.media = {
        ...comp.media,
        ...newMediaEntry,
      };
    } else if (checkFileName(files[i], 'logo')) {
      comp.logo = {
        ...comp.logo,
        ...newMediaEntry,
      };
    } else if (checkFileName(files[i], 'sponsor')) {
      comp.logo = {
        ...comp.logo,
        ...newMediaEntry,
      };
    } else if (checkFileName(files[i], 'promo')) {
      comp.promo = {
        ...comp.promo,
        ...newMediaEntry,
      };
    } else if (checkFileName(files[i], 'feature')) {
      comp.feature = {
        ...comp.feature,
        ...newMediaEntry,
      };
    } else if (checkFileName(files[i], 'complete')) {
      comp.completeView.media = {
        ...comp.completeView.media,
        ...newMediaEntry,
      };
    } else if (checkFileName(files[i], 'signup-complete')) {
      comp.signUpCompleteView.media = {
        ...comp.signUpCompleteView.media,
        ...newMediaEntry,
      };
    } else if (checkFileName(files[i], 'share')) {
      comp.shareDetails.openGraph = {
        ...comp.shareDetails.openGraph,
        ...newMediaEntry,
      };
    } else if (checkFileName(files[i], 'question-publisher-')) {
      const index = getQuestionNumberFromImageName(files[i].name) - 1;
      if (questions[index].link?.publisher) {
        questions[index].link = {
          ...questions[index].link,
          publisher: {
            ...questions[index].link?.publisher,
            ...newMediaEntry,
          },
        } as any;
      }
    } else if (checkFileName(files[i], 'question-link-')) {
      const index = getQuestionNumberFromImageName(files[i].name) - 1;
      questions[index].link = {
        ...questions[index].link,
        ...newMediaEntry,
      } as any;
    } else if (checkFileName(files[i], 'question-author-')) {
      const index = getQuestionNumberFromImageName(files[i].name) - 1;
      questions[index].author = {
        ...questions[index].author,
        ...newMediaEntry,
      } as any;
    } else if (checkFileName(files[i], 'question')) {
      const index = getQuestionNumberFromImageName(files[i].name) - 1;
      questions[index].media = {
        ...questions[index].media,
        ...newMediaEntry,
      };
    } else {
      log(`Skipping unexpected file [${files[0].name}].`);
    }
  }
};

export const uploadAssetForDocument = async (
  config: {protocol: string; cdnDomain: string},
  file: File,
  onProgress: (progress: number, message: string) => void,
): Promise<TMediaEntry> => {
  const name = file.name;
  const ext = name.split('.')[1];
  const type = file.type;
  const arrayBuffer = await file.arrayBuffer();
  // crypto-browserify (browser polyfill) requires Buffer or string, not Uint8Array
  const md5 = `${crypto.createHash('md5').update(Buffer.from(arrayBuffer) as any).digest('hex')}.${ext}`;
  const buffer = new Uint8Array(arrayBuffer); // Uint8Array for Firebase Storage ref.put()
  const ref = firebase.storage().ref().child(`media/${md5}`);

  onProgress(0, `${name}(${md5}) [${Math.round(file.size / 100000) / 10}M] `);
  try {
    await ref.getMetadata();
  } catch (err) {
    const task = ref.put(buffer, {contentType: type});
    task.on(
      firebase.storage.TaskEvent.STATE_CHANGED,
      (s) => onProgress(s.bytesTransferred / s.totalBytes, '🚀'),
      (e) => {
        throw e;
      },
    );
    await task;
  }

  onProgress(1, '✅\n');
  const url = `${config.protocol}//${config.cdnDomain}/media/${md5}`;
  if (file.type.startsWith('image')) {
    return {image: {url: url}};
  } else {
    return {video: {url: url}};
  }
};

// Attempt to retrieve the email notifications vendorListId from sendgrid
// by vendor if the comp doesn't specifiy one.
export const setEmailVendorListId = async (
  config: IConfig,
  readComp: TLocalComp,
  log: (message: string) => void,
) => {
  if (!readComp.privateData.emailNotifications.vendorListId) {
    try {
      log(`Get vendorListId from SendGrid for [${readComp.vendor}]\n`);
      const axRes = await axios.get(
        `${config.environment === 'local' ? `http://${config.adminDomain}` : window.location.origin}/emailContactsVendorListId?vendor=${readComp.vendor}`,
      );

      readComp.privateData.emailNotifications.vendorListId =
        axRes.data as string;
      if (!readComp.privateData.emailNotifications.vendorListId) {
        log(
          `Missing emailNotifications.vendorListId and we could not find one in SendGrid matching vendor [${readComp.vendor}].\n`,
        );
      }

      log(` (${readComp.privateData.emailNotifications.vendorListId})✅\n`);
    } catch (err) {
      log(
        `Unable to get vendorListId from SendGrid for [${readComp.vendor}].\n`,
      );
    }
  } else {
    log(
      `emailNotifications.vendorListId (${readComp.privateData.emailNotifications.vendorListId})✅\n`,
    );
  }
};

// Swap email template names for ids, if applicable
export const setEmailIdFromTemplateName = async (
  config: IConfig,
  readComp: TLocalComp,
  templateId:
    | 'templateGameEntry'
    | 'templateGameInPlay'
    | 'templateGameVerified'
    | 'templateGamePrize'
    | 'templateGameOutstandingQuestions'
    | 'templateGameOpen'
    | 'templateGamePreStart',
  log: (message: string) => void,
) => {
  if (readComp.privateData.emailNotifications[templateId]) {
    log(
      `Get ${templateId} for [${readComp.privateData.emailNotifications[templateId]}]`,
    );
    const axRes = await axios.get(
      `${config.environment === 'local' ? `http://${config.adminDomain}` : window.location.origin}/emailTemplateId?name=${readComp.privateData.emailNotifications[templateId]}`,
    );
    if (axRes.status !== 200) {
      throw new Error(
        `Unable to get ${templateId} for [${readComp.privateData.emailNotifications[templateId]}].`,
      );
    }

    readComp.privateData.emailNotifications[templateId] = axRes.data as string;
    log(` (${readComp.privateData.emailNotifications[templateId]})✅\n`);
  } else {
    log(
      `emailNotifications.${templateId} (${readComp.privateData.emailNotifications[templateId]})✅\n`,
    );
  }
};

export const fileSearch = (f: File, name: string) =>
  name.includes('.')
    ? f.name === name
    : (f.name.split('.')[0] === name || f.name.startsWith(`${name}_`)) &&
      ['png', 'jpg', 'jpeg', 'mp4'].some((v) => v === f.name.split('.')[1]);

export const readText = (
  file: File,
  log: (message: string) => void,
): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject;
    reader.onprogress = () => log('👀');
    reader.readAsText(file, 'utf8');
  });

const checkRequiredProperty = (
  name: string,
  value: any,
  log: (message: string) => void,
  errorMessage?: string,
) => {
  if (value === undefined) {
    throw new Error(
      errorMessage ?? `Required property [${name}] is missing from comp.json.`,
    );
  }

  log(`${name} ✅\n`);
};

const checkQuestions = (questions: TCompQuestion[]) => {
  questions.forEach((q) => {
    if (!Object.values(CompQuestionType).some((v) => v === q.type)) {
      throw new Error(`Invalid question type value [${q.type}]`);
    }
  });
};
